import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { AlertTriangle, Clock, Database, RefreshCw } from 'lucide-react'
import { memo } from 'react'

type MaintenanceModeProps = {
  title?: string
  message?: string
  estimatedTime?: string
  showRefreshButton?: boolean
  onRefresh?: () => void
  className?: string
}

export const MaintenanceMode = memo(function MaintenanceMode({
  title = 'System maintenance in progress',
  message = 'We are currently performing feature updates to improve your experience.',
  estimatedTime,
  showRefreshButton = true,
  onRefresh,
  className = '',
}: MaintenanceModeProps) {
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh()
    } else {
      window.location.reload()
    }
  }

  return (
    <div className={cn('flex min-h-screen items-center justify-center bg-background p-4', className)}>
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center space-y-6 p-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-caution/10">
            <AlertTriangle className="h-8! w-8! text-caution" />
          </div>

          <div>
            <h1 className="mb-2 text-2xl font-medium text-foreground">{title}</h1>
            <p className="text-sm text-muted-foreground">{message}</p>
          </div>

          <div className="flex w-full flex-col space-y-3">
            <div className="flex items-center justify-center space-x-2 rounded-lg bg-new/10 p-3">
              <Database className="h-4 w-4 text-new" />
              <span className="text-sm font-medium text-new">Updates in progress</span>
            </div>

            {estimatedTime && (
              <div className="flex items-center justify-center space-x-2 rounded-lg bg-muted p-3">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Estimated time: {estimatedTime}</span>
              </div>
            )}
          </div>

          {showRefreshButton && (
            <Button onClick={handleRefresh} variant="outline" className="w-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              Check status
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  )
})
